import { useState, useEffect, useCallback, useMemo, Component } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Users, Shield, Activity, Database, Clock, Server,
    RefreshCw, KeyRound, Upload, Eye, CheckCircle2, AlertCircle,
    Copy, ChevronRight, UserPlus, Ban, UserCheck, Building2,
    History, Search, ChevronLeft, Pencil, Power, PowerOff,
    BookOpen, GraduationCap, Trash2, Save
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminConsole() {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('users');
    const [users, setUsers] = useState([]);
    const [status, setStatus] = useState(null);
    const [oversight, setOversight] = useState(null);
    const [loading, setLoading] = useState(true);
    const [changingRole, setChangingRole] = useState(null);

    // Password reset state
    const [resetDialog, setResetDialog] = useState(false);
    const [resetTarget, setResetTarget] = useState(null);
    const [resetResult, setResetResult] = useState(null);
    const [resetting, setResetting] = useState(false);

    // Bulk enroll state
    const [bulkJson, setBulkJson] = useState('');
    const [bulkRole, setBulkRole] = useState('student');
    const [bulkResult, setBulkResult] = useState(null);
    const [bulkLoading, setBulkLoading] = useState(false);

    // Add user state
    const [addDialog, setAddDialog] = useState(false);
    const [addForm, setAddForm] = useState({ email: '', full_name: '', role: 'student', school_id: '' });
    const [addResult, setAddResult] = useState(null);
    const [adding, setAdding] = useState(false);

    // Schools state
    const [schools, setSchools] = useState([]);
    const [newSchoolName, setNewSchoolName] = useState('');
    const [creatingSchool, setCreatingSchool] = useState(false);
    const [bulkSchoolId, setBulkSchoolId] = useState('');
    const [schoolEditDialog, setSchoolEditDialog] = useState(false);
    const [schoolEditTarget, setSchoolEditTarget] = useState(null);
    const [schoolEditName, setSchoolEditName] = useState('');
    const [savingSchool, setSavingSchool] = useState(false);
    const [togglingSchool, setTogglingSchool] = useState(null);

    // FIX 2: User search/filter
    const [userSearch, setUserSearch] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [schoolFilter, setSchoolFilter] = useState('all');

    // FIX 3: Role change confirmation
    const [roleChangeDialog, setRoleChangeDialog] = useState(false);
    const [roleChangeTarget, setRoleChangeTarget] = useState(null);
    const [roleChangeNewRole, setRoleChangeNewRole] = useState('');

    // FIX 4: Disable confirmation
    const [disableDialog, setDisableDialog] = useState(false);
    const [disableTarget, setDisableTarget] = useState(null);
    const [disableReason, setDisableReason] = useState('');

    // FIX 5: Audit log state
    const [auditLogs, setAuditLogs] = useState([]);
    const [auditTotal, setAuditTotal] = useState(0);
    const [auditOffset, setAuditOffset] = useState(0);
    const [auditSearch, setAuditSearch] = useState('');
    const [auditDateFrom, setAuditDateFrom] = useState('');
    const [auditDateTo, setAuditDateTo] = useState('');

    // Class roster state
    const [adminCourses, setAdminCourses] = useState([]);
    const [coursesLoading, setCoursesLoading] = useState(false);
    const [selectedRosterCourseId, setSelectedRosterCourseId] = useState('');
    const [courseRoster, setCourseRoster] = useState([]);
    const [rosterLoading, setRosterLoading] = useState(false);
    const [rosterInstructorId, setRosterInstructorId] = useState('');
    const [selectedRosterStudentIds, setSelectedRosterStudentIds] = useState([]);
    const [rosterStudentSearch, setRosterStudentSearch] = useState('');
    const [rosterCourseSearch, setRosterCourseSearch] = useState('');
    const [rosterSchoolFilter, setRosterSchoolFilter] = useState('all');
    const [savingRosterInstructor, setSavingRosterInstructor] = useState(false);
    const [addingRosterStudents, setAddingRosterStudents] = useState(false);
    const [removingRosterStudentId, setRemovingRosterStudentId] = useState(null);

    const token = localStorage.getItem('token');
    const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
    const activeSchools = schools.filter(s => s.is_active !== false);

    const fetchData = useCallback(async () => {
        try {
            const [usersRes, statusRes, schoolsRes] = await Promise.all([
                axios.get(`${API}/admin/users`, { headers }),
                axios.get(`${API}/admin/system-status`, { headers }),
                axios.get(`${API}/admin/schools`, { headers }),
            ]);
            setUsers(usersRes.data);
            setStatus(statusRes.data);
            setSchools(schoolsRes.data);
        } catch (error) {
            if (error.response?.status === 403) toast.error('Admin access required');
            else toast.error('Failed to load admin data');
        } finally {
            setLoading(false);
        }
    }, [headers]);

    const fetchOversight = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/admin/oversight`, { headers });
            setOversight(res.data);
        } catch {
            toast.error('Failed to load oversight data');
        }
    }, [headers]);

    const fetchAuditLogs = useCallback(async () => {
        try {
            const params = new URLSearchParams({ limit: '50', offset: String(auditOffset) });
            if (auditSearch) params.append('search', auditSearch);
            if (auditDateFrom) params.append('date_from', auditDateFrom);
            if (auditDateTo) params.append('date_to', auditDateTo);
            const res = await axios.get(`${API}/admin/audit-logs?${params}`, { headers });
            setAuditLogs(res.data.logs);
            setAuditTotal(res.data.total);
        } catch { toast.error('Failed to load audit logs'); }
    }, [auditDateFrom, auditDateTo, auditOffset, auditSearch, headers]);

    const fetchAdminCourses = useCallback(async () => {
        setCoursesLoading(true);
        try {
            const res = await axios.get(`${API}/admin/courses`, { headers });
            const courses = Array.isArray(res.data) ? res.data : [];
            setAdminCourses(courses);
            if (courses.length > 0 && !selectedRosterCourseId) {
                setSelectedRosterCourseId(courses[0].id);
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to load class rosters');
        } finally {
            setCoursesLoading(false);
        }
    }, [headers, selectedRosterCourseId]);

    const fetchCourseRoster = useCallback(async (courseId) => {
        setRosterLoading(true);
        try {
            const res = await axios.get(`${API}/admin/courses/${courseId}/roster`, { headers });
            setCourseRoster(res.data.roster || []);
            setSelectedRosterStudentIds([]);
        } catch (error) {
            setCourseRoster([]);
            toast.error(error.response?.data?.detail || 'Failed to load course roster');
        } finally {
            setRosterLoading(false);
        }
    }, [headers]);

    useEffect(() => { fetchData(); }, [fetchData]);
    useEffect(() => { if (activeTab === 'oversight') fetchOversight(); }, [activeTab, fetchOversight]);
    useEffect(() => { if (activeTab === 'audit') fetchAuditLogs(); }, [activeTab, fetchAuditLogs]);
    useEffect(() => { if (activeTab === 'rosters') fetchAdminCourses(); }, [activeTab, fetchAdminCourses]);
    useEffect(() => { if (activeTab === 'rosters' && selectedRosterCourseId) fetchCourseRoster(selectedRosterCourseId); }, [activeTab, selectedRosterCourseId, fetchCourseRoster]);
    useEffect(() => {
        const selectedCourse = adminCourses.find(c => c.id === selectedRosterCourseId);
        if (selectedCourse) setRosterInstructorId(selectedCourse.instructor_id || '');
    }, [adminCourses, selectedRosterCourseId]);

    const handleAssignRosterInstructor = async () => {
        if (!selectedRosterCourseId) {
            toast.error('Select a course first');
            return;
        }
        if (!rosterInstructorId) {
            toast.error('Select an instructor');
            return;
        }
        setSavingRosterInstructor(true);
        try {
            const res = await axios.put(
                `${API}/admin/courses/${selectedRosterCourseId}/instructor`,
                { instructor_id: rosterInstructorId },
                { headers }
            );
            setAdminCourses(prev => prev.map(c => c.id === selectedRosterCourseId ? res.data : c));
            toast.success('Instructor assigned to course');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to assign instructor');
        } finally {
            setSavingRosterInstructor(false);
        }
    };

    const handleAddRosterStudents = async () => {
        if (!selectedRosterCourseId) {
            toast.error('Select a course first');
            return;
        }
        if (selectedRosterStudentIds.length === 0) {
            toast.error('Select at least one student');
            return;
        }
        setAddingRosterStudents(true);
        try {
            const res = await axios.post(
                `${API}/admin/courses/${selectedRosterCourseId}/roster`,
                { student_ids: selectedRosterStudentIds },
                { headers }
            );
            const summary = res.data.summary || {};
            toast.success(`Roster updated: ${summary.created || 0} added, ${summary.skipped || 0} skipped`);
            await fetchCourseRoster(selectedRosterCourseId);
            await fetchAdminCourses();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to add students to roster');
        } finally {
            setAddingRosterStudents(false);
        }
    };

    const handleRemoveRosterStudent = async (student) => {
        if (!selectedRosterCourseId || !student?.student_id) return;
        const label = student.student_name || student.student_email || student.student_id;
        if (!window.confirm(`Remove ${label} from this course roster?`)) return;
        setRemovingRosterStudentId(student.student_id);
        try {
            await axios.delete(`${API}/admin/courses/${selectedRosterCourseId}/roster/${student.student_id}`, { headers });
            toast.success('Student removed from roster');
            await fetchCourseRoster(selectedRosterCourseId);
            await fetchAdminCourses();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to remove student');
        } finally {
            setRemovingRosterStudentId(null);
        }
    };

    const requestRoleChange = (userId, newRole) => {
        const target = users.find(u => u.id === userId);
        if (!target) return;
        setRoleChangeTarget(target);
        setRoleChangeNewRole(newRole);
        setRoleChangeDialog(true);
    };

    const confirmRoleChange = async () => {
        if (!roleChangeTarget) return;
        setChangingRole(roleChangeTarget.id);
        setRoleChangeDialog(false);
        try {
            await axios.put(`${API}/admin/users/${roleChangeTarget.id}/role`, { role: roleChangeNewRole }, { headers });
            toast.success(`${roleChangeTarget.full_name} is now a ${roleChangeNewRole}`);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update role');
        } finally {
            setChangingRole(null);
            setRoleChangeTarget(null);
        }
    };

    const handleResetPassword = async () => {
        setResetting(true);
        try {
            const res = await axios.put(`${API}/admin/users/${resetTarget.id}/reset-password`, {}, { headers });
            setResetResult(res.data);
            toast.success('Password reset successfully');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to reset password');
        } finally {
            setResetting(false);
        }
    };

    const handleBulkEnroll = async () => {
        let parsed;
        try {
            parsed = JSON.parse(bulkJson);
            if (!Array.isArray(parsed)) parsed = [parsed];
        } catch {
            toast.error('Invalid JSON. Use format: [{"email":"a@b.com","full_name":"Name"}]');
            return;
        }
        setBulkLoading(true);
        try {
            const res = await axios.post(`${API}/admin/users/bulk`, {
                users: parsed,
                default_role: bulkRole,
                school_id: bulkSchoolId || undefined,
            }, { headers });
            setBulkResult(res.data);
            toast.success(`Bulk enroll: ${res.data.summary.created} created, ${res.data.summary.skipped} skipped`);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Bulk enroll failed');
        } finally {
            setBulkLoading(false);
        }
    };

    const handleCreateSchool = async () => {
        if (!newSchoolName.trim()) { toast.error('School name is required'); return; }
        setCreatingSchool(true);
        try {
            await axios.post(`${API}/admin/schools`, { name: newSchoolName.trim() }, { headers });
            toast.success(`School "${newSchoolName.trim()}" created`);
            setNewSchoolName('');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create school');
        } finally {
            setCreatingSchool(false);
        }
    };

    const requestEditSchool = (school) => {
        setSchoolEditTarget(school);
        setSchoolEditName(school.name || '');
        setSchoolEditDialog(true);
    };

    const handleUpdateSchool = async () => {
        if (!schoolEditTarget || !schoolEditName.trim()) {
            toast.error('School name is required');
            return;
        }
        setSavingSchool(true);
        try {
            await axios.put(`${API}/admin/schools/${schoolEditTarget.id}`, { name: schoolEditName.trim() }, { headers });
            toast.success(`School "${schoolEditName.trim()}" updated`);
            setSchoolEditDialog(false);
            setSchoolEditTarget(null);
            setSchoolEditName('');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update school');
        } finally {
            setSavingSchool(false);
        }
    };

    const handleToggleSchool = async (school) => {
        const action = school.is_active ? 'deactivate' : 'activate';
        setTogglingSchool(school.id);
        try {
            await axios.put(`${API}/admin/schools/${school.id}/${action}`, {}, { headers });
            toast.success(`${school.name} ${action === 'activate' ? 'activated' : 'deactivated'}`);
            if (school.id === bulkSchoolId) setBulkSchoolId('');
            if (school.id === addForm.school_id) setAddForm(p => ({ ...p, school_id: '' }));
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || `Failed to ${action} school`);
        } finally {
            setTogglingSchool(null);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard');
    };

    const handleAddUser = async () => {
        if (!addForm.email) { toast.error('Email is required'); return; }
        setAdding(true);
        try {
            const payload = { ...addForm };
            if (!payload.school_id) delete payload.school_id;
            const res = await axios.post(`${API}/admin/users`, payload, { headers });
            setAddResult(res.data);
            toast.success(`User ${res.data.email} created`);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create user');
        } finally {
            setAdding(false);
        }
    };

    const requestDisableUser = (u) => {
        setDisableTarget(u);
        setDisableReason('');
        setDisableDialog(true);
    };

    const confirmDisableUser = async () => {
        if (!disableTarget) return;
        setDisableDialog(false);
        try {
            await axios.put(`${API}/admin/users/${disableTarget.id}/disable`, { reason: disableReason || 'Administrative action' }, { headers });
            toast.success(`${disableTarget.full_name} has been disabled`);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to disable user');
        } finally {
            setDisableTarget(null);
        }
    };

    const handleEnableUser = async (u) => {
        try {
            await axios.put(`${API}/admin/users/${u.id}/enable`, {}, { headers });
            toast.success(`${u.full_name} has been re-enabled`);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to enable user');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-10 h-10 border-4 border-[#2F8FCB] border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (user?.role !== 'admin') {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <Shield className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-slate-700 mb-2">Access Denied</h2>
                    <p className="text-slate-500">Admin privileges required to access this page.</p>
                </div>
            </div>
        );
    }

    const tabs = [
        { id: 'users', label: 'Users', icon: Users },
        { id: 'schools', label: 'Schools', icon: Building2 },
        { id: 'bulk', label: 'Bulk Import', icon: Upload },
        { id: 'rosters', label: 'Class Rosters', icon: BookOpen },
        { id: 'oversight', label: 'Oversight', icon: Eye },
        { id: 'audit', label: 'Audit Log', icon: History },
    ];

    return (
        <div className="space-y-6 max-w-7xl mx-auto" data-testid="admin-console">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Admin Console</h1>
                    <p className="text-sm text-slate-500">System management and user administration</p>
                </div>
                <div className="flex gap-2">
                    <Button onClick={() => { setAddForm({ email: '', full_name: '', role: 'student', school_id: activeSchools[0]?.id || '' }); setAddResult(null); setAddDialog(true); }} className="bg-[#2F8FCB]" data-testid="add-user-btn">
                        <UserPlus className="w-4 h-4 mr-2" /> Add User
                    </Button>
                    <Button variant="outline" onClick={() => {
                        fetchData();
                        if (activeTab === 'oversight') fetchOversight();
                        if (activeTab === 'rosters') {
                            fetchAdminCourses();
                            if (selectedRosterCourseId) fetchCourseRoster(selectedRosterCourseId);
                        }
                    }} data-testid="admin-refresh-btn">
                        <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                    </Button>
                </div>
            </div>

            {/* System Status */}
            {status && <SystemStatusPanel status={status} />}

            {/* Tabs */}
            <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
                {tabs.map(t => (
                    <button
                        key={t.id}
                        onClick={() => setActiveTab(t.id)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                            activeTab === t.id ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'
                        }`}
                        data-testid={`admin-tab-${t.id}`}
                    >
                        <t.icon className="w-4 h-4" /> {t.label}
                    </button>
                ))}
            </div>

            {/* Tab Content */}
            {activeTab === 'users' && (
                <UserManagementPanel
                    users={users}
                    currentUser={user}
                    changingRole={changingRole}
                    onRoleChange={requestRoleChange}
                    onResetPassword={(u) => { setResetTarget(u); setResetResult(null); setResetDialog(true); }}
                    onDisable={requestDisableUser}
                    onEnable={handleEnableUser}
                    schools={schools}
                    userSearch={userSearch}
                    setUserSearch={setUserSearch}
                    roleFilter={roleFilter}
                    setRoleFilter={setRoleFilter}
                    schoolFilter={schoolFilter}
                    setSchoolFilter={setSchoolFilter}
                />
            )}

            {activeTab === 'schools' && (
                <SchoolsErrorBoundary key="schools">
                    <SchoolsPanel
                        schools={schools}
                        newSchoolName={newSchoolName}
                        setNewSchoolName={setNewSchoolName}
                        creatingSchool={creatingSchool}
                        onCreateSchool={handleCreateSchool}
                        onEditSchool={requestEditSchool}
                        onToggleSchool={handleToggleSchool}
                        togglingSchool={togglingSchool}
                    />
                </SchoolsErrorBoundary>
            )}

            {activeTab === 'bulk' && (
                <BulkEnrollPanel
                    bulkJson={bulkJson}
                    setBulkJson={setBulkJson}
                    bulkRole={bulkRole}
                    setBulkRole={setBulkRole}
                    bulkResult={bulkResult}
                    bulkLoading={bulkLoading}
                    onEnroll={handleBulkEnroll}
                    copyToClipboard={copyToClipboard}
                    schools={activeSchools}
                    bulkSchoolId={bulkSchoolId}
                    setBulkSchoolId={setBulkSchoolId}
                />
            )}

            {activeTab === 'rosters' && (
                <ClassRostersPanel
                    courses={adminCourses}
                    coursesLoading={coursesLoading}
                    selectedCourseId={selectedRosterCourseId}
                    setSelectedCourseId={setSelectedRosterCourseId}
                    roster={courseRoster}
                    rosterLoading={rosterLoading}
                    users={users}
                    schools={schools}
                    instructorId={rosterInstructorId}
                    setInstructorId={setRosterInstructorId}
                    selectedStudentIds={selectedRosterStudentIds}
                    setSelectedStudentIds={setSelectedRosterStudentIds}
                    studentSearch={rosterStudentSearch}
                    setStudentSearch={setRosterStudentSearch}
                    courseSearch={rosterCourseSearch}
                    setCourseSearch={setRosterCourseSearch}
                    schoolFilter={rosterSchoolFilter}
                    setSchoolFilter={setRosterSchoolFilter}
                    savingInstructor={savingRosterInstructor}
                    addingStudents={addingRosterStudents}
                    removingStudentId={removingRosterStudentId}
                    onAssignInstructor={handleAssignRosterInstructor}
                    onAddStudents={handleAddRosterStudents}
                    onRemoveStudent={handleRemoveRosterStudent}
                    onRefresh={() => {
                        fetchAdminCourses();
                        if (selectedRosterCourseId) fetchCourseRoster(selectedRosterCourseId);
                    }}
                />
            )}

            {activeTab === 'oversight' && (
                <OversightPanel oversight={oversight} />
            )}

            {activeTab === 'audit' && (
                <AuditLogPanel
                    logs={auditLogs}
                    total={auditTotal}
                    offset={auditOffset}
                    setOffset={setAuditOffset}
                    search={auditSearch}
                    setSearch={setAuditSearch}
                    dateFrom={auditDateFrom}
                    setDateFrom={setAuditDateFrom}
                    dateTo={auditDateTo}
                    setDateTo={setAuditDateTo}
                />
            )}

            {/* Reset Password Dialog */}
            <Dialog open={resetDialog} onOpenChange={setResetDialog}>
                <DialogContent className="max-w-md" aria-describedby="reset-pw-desc">
                    <DialogHeader>
                        <DialogTitle className="text-amber-700">Reset User Password</DialogTitle>
                    </DialogHeader>
                    {!resetResult ? (
                        <>
                            <p id="reset-pw-desc" className="text-sm text-slate-600 mb-4">
                                This will generate a temporary password. The user will be required to change it on next login.
                            </p>
                            {resetTarget && (
                                <div className="bg-slate-50 rounded-lg p-3 mb-4">
                                    <p className="text-sm"><strong>User:</strong> {resetTarget.full_name}</p>
                                    <p className="text-sm"><strong>Email:</strong> {resetTarget.email}</p>
                                    <p className="text-sm"><strong>Role:</strong> {resetTarget.role}</p>
                                </div>
                            )}
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setResetDialog(false)}>Cancel</Button>
                                <Button onClick={handleResetPassword} disabled={resetting} className="bg-amber-600 hover:bg-amber-700" data-testid="confirm-reset-pw-btn">
                                    {resetting ? 'Resetting...' : 'Reset Password'}
                                </Button>
                            </DialogFooter>
                        </>
                    ) : (
                        <>
                            <p className="text-sm text-slate-600 mb-2">Password has been reset. Share this temporary password with the user:</p>
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                                <div className="flex items-center justify-between">
                                    <code className="text-lg font-mono font-bold text-amber-800" data-testid="temp-password-display">
                                        {resetResult.temp_password}
                                    </code>
                                    <Button variant="ghost" size="sm" onClick={() => copyToClipboard(resetResult.temp_password)} data-testid="copy-temp-pw-btn">
                                        <Copy className="w-4 h-4" />
                                    </Button>
                                </div>
                                <p className="text-xs text-amber-600 mt-2">This password is shown only once. The user must change it on next login.</p>
                            </div>
                            <DialogFooter>
                                <Button onClick={() => { setResetDialog(false); fetchData(); }}>Done</Button>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Add User Dialog */}
            <Dialog open={addDialog} onOpenChange={setAddDialog}>
                <DialogContent className="max-w-md" aria-describedby="add-user-desc">
                    <DialogHeader>
                        <DialogTitle>Add New User</DialogTitle>
                    </DialogHeader>
                    {!addResult ? (
                        <>
                            <p id="add-user-desc" className="text-sm text-slate-600 mb-4">
                                Create a new user account. A temporary password will be generated.
                            </p>
                            <div className="space-y-3">
                                <div>
                                    <Label>Email *</Label>
                                    <Input value={addForm.email} onChange={(e) => setAddForm(p => ({ ...p, email: e.target.value }))} placeholder="user@university.edu" className="mt-1" data-testid="add-user-email" />
                                </div>
                                <div>
                                    <Label>Full Name</Label>
                                    <Input value={addForm.full_name} onChange={(e) => setAddForm(p => ({ ...p, full_name: e.target.value }))} placeholder="Optional" className="mt-1" data-testid="add-user-name" />
                                </div>
                                <div>
                                    <Label>Role</Label>
                                    <Select value={addForm.role} onValueChange={(v) => setAddForm(p => ({ ...p, role: v }))}>
                                        <SelectTrigger className="mt-1" data-testid="add-user-role"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="student">Student</SelectItem>
                                            <SelectItem value="instructor">Instructor</SelectItem>
                                            <SelectItem value="auditor">Auditor</SelectItem>
                                            <SelectItem value="admin">Admin</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                {addForm.role !== 'admin' && activeSchools.length > 0 && (
                                    <div>
                                        <Label>School</Label>
                                        <Select value={addForm.school_id} onValueChange={(v) => setAddForm(p => ({ ...p, school_id: v }))}>
                                            <SelectTrigger className="mt-1" data-testid="add-user-school"><SelectValue placeholder="Select school" /></SelectTrigger>
                                            <SelectContent>
                                                {activeSchools.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}
                            </div>
                            <DialogFooter className="mt-4">
                                <Button variant="outline" onClick={() => setAddDialog(false)}>Cancel</Button>
                                <Button onClick={handleAddUser} disabled={adding || !addForm.email} className="bg-[#2F8FCB]" data-testid="confirm-add-user-btn">
                                    {adding ? 'Creating...' : 'Create User'}
                                </Button>
                            </DialogFooter>
                        </>
                    ) : (
                        <>
                            <p className="text-sm text-slate-600 mb-2">User created successfully. Share the temporary password:</p>
                            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
                                <p className="text-sm mb-1"><strong>Email:</strong> {addResult.email}</p>
                                <p className="text-sm mb-2"><strong>Role:</strong> {addResult.role}</p>
                                <div className="flex items-center justify-between bg-white rounded px-3 py-2 border">
                                    <code className="text-lg font-mono font-bold text-emerald-800" data-testid="new-user-temp-pw">{addResult.temp_password}</code>
                                    <Button variant="ghost" size="sm" onClick={() => copyToClipboard(addResult.temp_password)}><Copy className="w-4 h-4" /></Button>
                                </div>
                                <p className="text-xs text-emerald-600 mt-2">This password is shown only once. User must change it on first login.</p>
                            </div>
                            <DialogFooter>
                                <Button onClick={() => setAddDialog(false)}>Done</Button>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Edit School Dialog */}
            <Dialog open={schoolEditDialog} onOpenChange={setSchoolEditDialog}>
                <DialogContent className="max-w-md" aria-describedby="edit-school-desc">
                    <DialogHeader>
                        <DialogTitle>Edit School</DialogTitle>
                    </DialogHeader>
                    <p id="edit-school-desc" className="text-sm text-slate-600">
                        Update the school name used across admin filters and user records.
                    </p>
                    <div className="mt-3">
                        <Label>School Name</Label>
                        <Input
                            value={schoolEditName}
                            onChange={(e) => setSchoolEditName(e.target.value)}
                            className="mt-1"
                            data-testid="edit-school-name"
                        />
                    </div>
                    <DialogFooter className="mt-4">
                        <Button variant="outline" onClick={() => setSchoolEditDialog(false)} className="rounded-full">Cancel</Button>
                        <Button onClick={handleUpdateSchool} disabled={savingSchool || !schoolEditName.trim()} className="bg-[#2F8FCB] rounded-full" data-testid="confirm-edit-school-btn">
                            {savingSchool ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* FIX 3: Role Change Confirmation Dialog */}
            <Dialog open={roleChangeDialog} onOpenChange={setRoleChangeDialog}>
                <DialogContent aria-describedby="role-change-desc">
                    <DialogHeader>
                        <DialogTitle className="font-['Manrope']">Change User Role</DialogTitle>
                    </DialogHeader>
                    <p id="role-change-desc" className="text-sm text-slate-600">
                        Change <strong>{roleChangeTarget?.full_name}</strong> from <strong className="capitalize">{roleChangeTarget?.role}</strong> to <strong className="capitalize">{roleChangeNewRole}</strong>? This affects what they can access in MaceSuite.
                    </p>
                    <DialogFooter className="mt-4">
                        <Button variant="outline" onClick={() => setRoleChangeDialog(false)} className="rounded-full">Cancel</Button>
                        <Button onClick={confirmRoleChange} className="bg-[#2F8FCB] rounded-full" data-testid="confirm-role-change-btn">Confirm</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* FIX 4: Disable User Confirmation Dialog */}
            <Dialog open={disableDialog} onOpenChange={setDisableDialog}>
                <DialogContent aria-describedby="disable-user-desc">
                    <DialogHeader>
                        <DialogTitle className="text-red-700 font-['Manrope']">Disable User</DialogTitle>
                    </DialogHeader>
                    <p id="disable-user-desc" className="text-sm text-slate-600">
                        Disable <strong>{disableTarget?.full_name}</strong> ({disableTarget?.email})? They will be logged out immediately and cannot sign in until re-enabled.
                    </p>
                    <div className="mt-3">
                        <Label>Reason (optional)</Label>
                        <Input value={disableReason} onChange={(e) => setDisableReason(e.target.value)} placeholder="Administrative action" className="mt-1" data-testid="disable-reason-input" />
                    </div>
                    <DialogFooter className="mt-4">
                        <Button variant="outline" onClick={() => setDisableDialog(false)} className="rounded-full">Cancel</Button>
                        <Button onClick={confirmDisableUser} className="bg-red-600 hover:bg-red-700 rounded-full" data-testid="confirm-disable-btn">Disable</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// ── Sub-components ────────────────────────────────────────

function SystemStatusPanel({ status }) {
    return (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="system-status-panel">
            <div className="bg-gradient-to-r from-[#0F2A44] to-[#1E3A8A] px-6 py-3 text-white">
                <h2 className="font-semibold text-sm flex items-center gap-2"><Activity className="w-4 h-4" /> System Status</h2>
            </div>
            <div className="p-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                    <StatusCard label="API" value={status.api_status} ok={status.api_status === 'ok'} icon={Server} />
                    <StatusCard label="Database" value={status.db_status} ok={status.db_status === 'connected'} icon={Database} />
                    <StatusCard label="Version" value={status.version} ok icon={Shield} />
                    <StatusCard label="Server Time" value={new Date(status.server_time).toLocaleTimeString()} ok icon={Clock} />
                </div>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                    {Object.entries(status.counts).map(([key, val]) => (
                        <div key={key} className="bg-slate-50 rounded-lg p-2 text-center">
                            <p className="text-base font-bold text-slate-900">{val}</p>
                            <p className="text-[10px] text-slate-500 capitalize">{key.replace(/_/g, ' ')}</p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function StatusCard({ label, value, ok, icon: Icon }) {
    return (
        <div className={`rounded-lg border p-3 ${ok ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}>
            <div className="flex items-center gap-1.5 mb-1">
                <Icon className={`w-3.5 h-3.5 ${ok ? 'text-emerald-600' : 'text-red-600'}`} />
                <span className="text-xs text-slate-500">{label}</span>
            </div>
            <p className={`text-sm font-bold ${ok ? 'text-emerald-700' : 'text-red-700'}`}>{value}</p>
        </div>
    );
}

function UserManagementPanel({ users, currentUser, changingRole, onRoleChange, onResetPassword, onDisable, onEnable, schools, userSearch, setUserSearch, roleFilter, setRoleFilter, schoolFilter, setSchoolFilter }) {
    const roleCounts = {
        admin: users.filter(u => u.role === 'admin').length,
        instructor: users.filter(u => u.role === 'instructor').length,
        student: users.filter(u => u.role === 'student').length,
    };

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
                {Object.entries(roleCounts).map(([role, count]) => (
                    <div key={role} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                        <div className="flex items-center gap-3">
                            <div className={`w-9 h-9 rounded-lg text-white flex items-center justify-center ${
                                role === 'admin' ? 'bg-red-500' : role === 'instructor' ? 'bg-blue-500' : 'bg-emerald-500'
                            }`}><Users className="w-4 h-4" /></div>
                            <div>
                                <p className="text-xl font-bold text-slate-900">{count}</p>
                                <p className="text-xs text-slate-500 capitalize">{role}s</p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            {/* FIX 2: Search + Filter bar */}
            <div className="flex flex-col sm:flex-row gap-2 mb-0">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input placeholder="Search name or email..." value={userSearch} onChange={(e) => setUserSearch(e.target.value)} className="pl-9 h-9 text-sm" data-testid="user-search-input" />
                </div>
                <Select value={roleFilter} onValueChange={setRoleFilter}>
                    <SelectTrigger className="w-36 h-9 text-xs"><SelectValue placeholder="All Roles" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Roles</SelectItem>
                        <SelectItem value="auditor">Auditor</SelectItem>
                                            <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="instructor">Instructor</SelectItem>
                        <SelectItem value="student">Student</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={schoolFilter} onValueChange={setSchoolFilter}>
                    <SelectTrigger className="w-44 h-9 text-xs"><SelectValue placeholder="All Schools" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Schools</SelectItem>
                        {(schools || []).map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>

            {(() => {
                const q = userSearch.toLowerCase();
                const filtered = users.filter(u => {
                    if (q && !u.full_name?.toLowerCase().includes(q) && !u.email?.toLowerCase().includes(q)) return false;
                    if (roleFilter !== 'all' && u.role !== roleFilter) return false;
                    if (schoolFilter !== 'all' && u.school_id !== schoolFilter) return false;
                    return true;
                });
                return (
                <>
                <p className="text-xs text-slate-400 mt-1 mb-2">Showing {filtered.length} of {users.length} users</p>
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="user-management-panel">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                            <tr>
                                <th className="text-left px-4 py-2.5">Name</th>
                                <th className="text-left px-4 py-2.5">Email</th>
                                <th className="text-left px-4 py-2.5">School</th>
                                <th className="text-center px-4 py-2.5">Role</th>
                                <th className="text-center px-4 py-2.5">Change Role</th>
                                <th className="text-center px-4 py-2.5">Actions</th>
                                <th className="text-left px-4 py-2.5">Joined</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {filtered.map(u => {
                                const isDisabled = u.is_active === false;
                                return (
                                <tr key={u.id} className={`hover:bg-slate-50 ${isDisabled ? 'opacity-60' : ''}`} data-testid={`user-row-${u.id}`}>
                                    <td className="px-4 py-2.5">
                                        <div className="flex items-center gap-2">
                                            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold ${
                                                isDisabled ? 'bg-slate-400' :
                                                u.role === 'admin' ? 'bg-red-500' : u.role === 'auditor' ? 'bg-purple-500' : u.role === 'instructor' ? 'bg-blue-500' : 'bg-emerald-500'
                                            }`}>{u.full_name?.charAt(0).toUpperCase()}</div>
                                            <span className={`font-medium text-sm ${isDisabled ? 'text-slate-400 line-through' : 'text-slate-900'}`}>{u.full_name}</span>
                                            {isDisabled && (
                                                <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-[10px] rounded font-medium" data-testid={`disabled-badge-${u.id}`}>Disabled</span>
                                            )}
                                            {u.must_change_password && !isDisabled && (
                                                <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] rounded font-medium">Temp PW</span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-4 py-2.5 text-slate-600 text-xs">{u.email}</td>
                                    <td className="px-4 py-2.5 text-xs text-slate-500">{u.school_name || <span className="italic text-slate-300">—</span>}</td>
                                    <td className="px-4 py-2.5 text-center">
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                            u.role === 'admin' ? 'bg-red-100 text-red-700' :
                                            u.role === 'auditor' ? 'bg-purple-100 text-purple-700' :
                                            u.role === 'instructor' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'
                                        }`}>{u.role}</span>
                                    </td>
                                    <td className="px-4 py-2.5 text-center">
                                        {u.id === currentUser.id ? (
                                            <span className="text-xs text-slate-400 italic">You</span>
                                        ) : isDisabled ? (
                                            <span className="text-xs text-slate-400 italic">Disabled</span>
                                        ) : (
                                            <Select value={u.role} onValueChange={(val) => onRoleChange(u.id, val)} disabled={changingRole === u.id}>
                                                <SelectTrigger className="w-28 h-7 text-xs mx-auto" data-testid={`role-select-${u.id}`}>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="student">Student</SelectItem>
                                                    <SelectItem value="instructor">Instructor</SelectItem>
                                                    <SelectItem value="auditor">Auditor</SelectItem>
                                            <SelectItem value="admin">Admin</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        )}
                                    </td>
                                    <td className="px-4 py-2.5 text-center">
                                        {u.id !== currentUser.id && (
                                            <div className="flex items-center justify-center gap-0.5">
                                                {!isDisabled && (
                                                    <Button variant="ghost" size="sm" className="text-amber-600 h-7 text-xs" onClick={() => onResetPassword(u)} data-testid={`reset-pw-${u.id}`}>
                                                        <KeyRound className="w-3.5 h-3.5 mr-1" /> Reset
                                                    </Button>
                                                )}
                                                {isDisabled ? (
                                                    <Button variant="ghost" size="sm" className="text-emerald-600 h-7 text-xs" onClick={() => onEnable(u)} data-testid={`enable-user-${u.id}`}>
                                                        <UserCheck className="w-3.5 h-3.5 mr-1" /> Enable
                                                    </Button>
                                                ) : (
                                                    <Button variant="ghost" size="sm" className="text-red-500 h-7 text-xs" onClick={() => onDisable(u)} data-testid={`disable-user-${u.id}`}>
                                                        <Ban className="w-3.5 h-3.5 mr-1" /> Disable
                                                    </Button>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-2.5 text-slate-500 text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}</td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
            </>
                );
            })()}
        </div>
    );
}

function BulkEnrollPanel({ bulkJson, setBulkJson, bulkRole, setBulkRole, bulkResult, bulkLoading, onEnroll, copyToClipboard, schools, bulkSchoolId, setBulkSchoolId }) {
    const sampleJson = JSON.stringify([
        { email: "student1@university.edu", full_name: "Student One" },
        { email: "student2@university.edu", full_name: "Student Two" },
    ], null, 2);

    return (
        <div className="space-y-4">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-testid="bulk-enroll-panel">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Upload className="w-5 h-5" /> Bulk Import Users
                </h3>
                <p className="text-sm text-slate-500 mb-4">Create multiple user accounts at once by providing a list of names and emails.</p>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div>
                        <Label>User List (JSON)</Label>
                        <Textarea
                            value={bulkJson}
                            onChange={(e) => setBulkJson(e.target.value)}
                            placeholder={sampleJson}
                            rows={8}
                            className="mt-1 font-mono text-xs"
                            data-testid="bulk-json-input"
                        />
                        <p className="text-xs text-slate-400 mt-1">Format: [{`{"email":"...","full_name":"..."}`}]</p>
                    </div>
                    <div className="space-y-4">
                        <div>
                            <Label>Default Role</Label>
                            <Select value={bulkRole} onValueChange={setBulkRole}>
                                <SelectTrigger className="mt-1 w-48" data-testid="bulk-role-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="student">Student</SelectItem>
                                    <SelectItem value="instructor">Instructor</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        {schools.length > 0 && (
                            <div>
                                <Label>School</Label>
                                <Select value={bulkSchoolId} onValueChange={setBulkSchoolId}>
                                    <SelectTrigger className="mt-1 w-48" data-testid="bulk-school-select">
                                        <SelectValue placeholder="Default School" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {schools.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        <Button onClick={onEnroll} disabled={bulkLoading || !bulkJson.trim()} className="bg-[#2F8FCB]" data-testid="bulk-enroll-btn">
                            <Upload className="w-4 h-4 mr-2" />
                            {bulkLoading ? 'Enrolling...' : 'Enroll Users'}
                        </Button>
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                            <p className="text-xs text-amber-700">Temporary passwords are shown once after creation. Users must change them on first login.</p>
                        </div>
                    </div>
                </div>
            </div>

            {bulkResult && (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="bulk-results">
                    <div className="px-5 py-3 border-b border-slate-200 bg-slate-50">
                        <h3 className="font-semibold text-sm">
                            Results: {bulkResult.summary.created} created, {bulkResult.summary.skipped} skipped, {bulkResult.summary.errors} errors
                        </h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                                <tr>
                                    <th className="text-left px-4 py-2">Email</th>
                                    <th className="text-left px-4 py-2">Name</th>
                                    <th className="text-center px-4 py-2">Status</th>
                                    <th className="text-left px-4 py-2">Temp Password</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {bulkResult.results.created.map((u, i) => (
                                    <tr key={i} className="hover:bg-slate-50">
                                        <td className="px-4 py-2">{u.email}</td>
                                        <td className="px-4 py-2">{u.full_name}</td>
                                        <td className="px-4 py-2 text-center"><span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs">Created</span></td>
                                        <td className="px-4 py-2">
                                            <div className="flex items-center gap-1">
                                                <code className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded">{u.temp_password}</code>
                                                <button onClick={() => copyToClipboard(u.temp_password)} className="text-slate-400 hover:text-slate-600"><Copy className="w-3.5 h-3.5" /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {bulkResult.results.skipped.map((u, i) => (
                                    <tr key={`s-${i}`} className="hover:bg-slate-50">
                                        <td className="px-4 py-2">{u.email}</td>
                                        <td className="px-4 py-2">-</td>
                                        <td className="px-4 py-2 text-center"><span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs">Skipped</span></td>
                                        <td className="px-4 py-2 text-xs text-slate-400">{u.reason}</td>
                                    </tr>
                                ))}
                                {bulkResult.results.errors.map((u, i) => (
                                    <tr key={`e-${i}`} className="hover:bg-red-50">
                                        <td className="px-4 py-2">{u.email}</td>
                                        <td className="px-4 py-2">-</td>
                                        <td className="px-4 py-2 text-center"><span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs">Error</span></td>
                                        <td className="px-4 py-2 text-xs text-red-500">{u.reason}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

function ClassRostersPanel({
    courses,
    coursesLoading,
    selectedCourseId,
    setSelectedCourseId,
    roster,
    rosterLoading,
    users,
    schools,
    instructorId,
    setInstructorId,
    selectedStudentIds,
    setSelectedStudentIds,
    studentSearch,
    setStudentSearch,
    courseSearch,
    setCourseSearch,
    schoolFilter,
    setSchoolFilter,
    savingInstructor,
    addingStudents,
    removingStudentId,
    onAssignInstructor,
    onAddStudents,
    onRemoveStudent,
    onRefresh,
}) {
    const schoolById = new Map((schools || []).map(s => [s.id, s]));
    const selectedCourse = (courses || []).find(c => c.id === selectedCourseId);
    const rosterStudentIds = new Set((roster || []).map(row => row.student_id));
    const activeSchools = (schools || []).filter(s => s.is_active !== false);

    const courseQuery = courseSearch.trim().toLowerCase();
    const filteredCourses = (courses || []).filter(course => {
        if (schoolFilter !== 'all' && course.school_id !== schoolFilter) return false;
        if (!courseQuery) return true;
        const haystack = [
            course.code,
            course.title,
            course.instructor_name,
            course.instructor_email,
            course.school_name,
        ].filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(courseQuery);
    });

    const instructors = (users || [])
        .filter(u => u.role === 'instructor' && u.is_active !== false)
        .filter(u => !selectedCourse?.school_id || !u.school_id || u.school_id === selectedCourse.school_id)
        .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));

    const studentQuery = studentSearch.trim().toLowerCase();
    const availableStudents = (users || [])
        .filter(u => u.role === 'student' && u.is_active !== false)
        .filter(u => !rosterStudentIds.has(u.id))
        .filter(u => !selectedCourse?.school_id || !u.school_id || u.school_id === selectedCourse.school_id)
        .filter(u => {
            if (!studentQuery) return true;
            return `${u.full_name || ''} ${u.email || ''}`.toLowerCase().includes(studentQuery);
        })
        .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));

    const toggleStudent = (studentId) => {
        setSelectedStudentIds(prev => (
            prev.includes(studentId)
                ? prev.filter(id => id !== studentId)
                : [...prev, studentId]
        ));
    };

    const selectCourse = (courseId) => {
        setSelectedCourseId(courseId);
        setSelectedStudentIds([]);
        setStudentSearch('');
    };

    return (
        <div className="space-y-4" data-testid="class-rosters-panel">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                    <div>
                        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                            <GraduationCap className="w-5 h-5 text-[#2F8FCB]" />
                            Class Rosters
                        </h3>
                        <p className="text-sm text-slate-500 mt-1">Assign instructors and manage enrolled students by course section.</p>
                    </div>
                    <Button variant="outline" onClick={onRefresh} disabled={coursesLoading || rosterLoading} data-testid="rosters-refresh-btn">
                        <RefreshCw className={`w-4 h-4 mr-2 ${(coursesLoading || rosterLoading) ? 'animate-spin' : ''}`} />
                        Refresh Rosters
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-[360px_1fr] gap-4">
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="p-4 border-b border-slate-100 space-y-3">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                                value={courseSearch}
                                onChange={(e) => setCourseSearch(e.target.value)}
                                placeholder="Search courses..."
                                className="pl-9 h-9"
                                data-testid="roster-course-search"
                            />
                        </div>
                        <Select value={schoolFilter} onValueChange={setSchoolFilter}>
                            <SelectTrigger className="h-9 text-sm" data-testid="roster-school-filter">
                                <SelectValue placeholder="All Schools" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Schools</SelectItem>
                                {activeSchools.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="max-h-[620px] overflow-y-auto divide-y divide-slate-100">
                        {coursesLoading ? (
                            <div className="p-6 text-center text-sm text-slate-500">Loading courses...</div>
                        ) : filteredCourses.length === 0 ? (
                            <div className="p-6 text-center text-sm text-slate-500">No courses match the filters.</div>
                        ) : filteredCourses.map(course => {
                            const active = course.id === selectedCourseId;
                            return (
                                <button
                                    key={course.id}
                                    type="button"
                                    onClick={() => selectCourse(course.id)}
                                    className={`w-full text-left p-4 transition-colors ${active ? 'bg-blue-50 border-l-4 border-[#2F8FCB]' : 'hover:bg-slate-50 border-l-4 border-transparent'}`}
                                    data-testid={`roster-course-${course.id}`}
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div>
                                            <p className="text-sm font-semibold text-slate-900">{course.code || 'Course'} - {course.title}</p>
                                            <p className="text-xs text-slate-500 mt-1">{course.school_name || schoolById.get(course.school_id)?.name || 'No school assigned'}</p>
                                        </div>
                                        <ChevronRight className={`w-4 h-4 mt-1 ${active ? 'text-[#2F8FCB]' : 'text-slate-300'}`} />
                                    </div>
                                    <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                                        <div className="bg-white rounded-md border border-slate-100 px-2 py-1.5">
                                            <span className="text-slate-400">Instructor</span>
                                            <p className="font-medium text-slate-700 truncate">{course.instructor_name || 'Unassigned'}</p>
                                        </div>
                                        <div className="bg-white rounded-md border border-slate-100 px-2 py-1.5">
                                            <span className="text-slate-400">Students</span>
                                            <p className="font-medium text-slate-700">{course.enrolled_count || 0}</p>
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="space-y-4">
                    {!selectedCourse ? (
                        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 text-center text-slate-500">
                            Select a course to manage its instructor and student roster.
                        </div>
                    ) : (
                        <>
                            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                                    <div>
                                        <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Selected Course</p>
                                        <h3 className="text-lg font-bold text-slate-900 mt-1">{selectedCourse.code || 'Course'} - {selectedCourse.title}</h3>
                                        <p className="text-sm text-slate-500">{selectedCourse.school_name || schoolById.get(selectedCourse.school_id)?.name || 'No school assigned'}</p>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3 min-w-[220px]">
                                        <div className="bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">
                                            <p className="text-xs text-slate-400">Roster</p>
                                            <p className="text-xl font-bold text-slate-900">{roster.length}</p>
                                        </div>
                                        <div className="bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">
                                            <p className="text-xs text-slate-400">Instructor</p>
                                            <p className="text-sm font-semibold text-slate-900 truncate">{selectedCourse.instructor_name || 'Unassigned'}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-5 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3 items-end">
                                    <div>
                                        <Label>Assigned Instructor</Label>
                                        <Select value={instructorId} onValueChange={setInstructorId}>
                                            <SelectTrigger className="mt-1" data-testid="roster-instructor-select">
                                                <SelectValue placeholder="Select instructor" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {instructors.map(instructor => (
                                                    <SelectItem key={instructor.id} value={instructor.id}>
                                                        {instructor.full_name} - {instructor.email}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {instructors.length === 0 && (
                                            <p className="text-xs text-amber-600 mt-1">No active instructor is available for this course school.</p>
                                        )}
                                    </div>
                                    <Button
                                        onClick={onAssignInstructor}
                                        disabled={savingInstructor || !instructorId}
                                        className="bg-[#2F8FCB]"
                                        data-testid="roster-save-instructor-btn"
                                    >
                                        <Save className="w-4 h-4 mr-2" />
                                        {savingInstructor ? 'Saving...' : 'Save Instructor'}
                                    </Button>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                                    <div className="p-4 border-b border-slate-100">
                                        <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                                            <UserPlus className="w-4 h-4 text-[#2F8FCB]" />
                                            Add Students
                                        </h4>
                                        <div className="relative mt-3">
                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                            <Input
                                                value={studentSearch}
                                                onChange={(e) => setStudentSearch(e.target.value)}
                                                placeholder="Search available students..."
                                                className="pl-9 h-9"
                                                data-testid="roster-student-search"
                                            />
                                        </div>
                                    </div>
                                    <div className="max-h-[340px] overflow-y-auto divide-y divide-slate-100">
                                        {availableStudents.length === 0 ? (
                                            <div className="p-6 text-center text-sm text-slate-500">No available students found.</div>
                                        ) : availableStudents.map(student => {
                                            const checked = selectedStudentIds.includes(student.id);
                                            return (
                                                <label key={student.id} className="flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer" data-testid={`roster-student-option-${student.id}`}>
                                                    <input
                                                        type="checkbox"
                                                        checked={checked}
                                                        onChange={() => toggleStudent(student.id)}
                                                        className="w-4 h-4 rounded border-slate-300"
                                                    />
                                                    <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold">
                                                        {(student.full_name || student.email || 'S').charAt(0).toUpperCase()}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <p className="text-sm font-medium text-slate-900 truncate">{student.full_name || 'Unnamed Student'}</p>
                                                        <p className="text-xs text-slate-500 truncate">{student.email}</p>
                                                    </div>
                                                </label>
                                            );
                                        })}
                                    </div>
                                    <div className="p-4 border-t border-slate-100 flex items-center justify-between gap-3">
                                        <p className="text-xs text-slate-500">{selectedStudentIds.length} selected</p>
                                        <Button
                                            onClick={onAddStudents}
                                            disabled={addingStudents || selectedStudentIds.length === 0}
                                            className="bg-[#2F8FCB]"
                                            data-testid="roster-add-students-btn"
                                        >
                                            <UserCheck className="w-4 h-4 mr-2" />
                                            {addingStudents ? 'Adding...' : 'Add to Roster'}
                                        </Button>
                                    </div>
                                </div>

                                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                                    <div className="p-4 border-b border-slate-100">
                                        <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                                            <Users className="w-4 h-4 text-[#2F8FCB]" />
                                            Current Roster
                                        </h4>
                                    </div>
                                    <div className="max-h-[422px] overflow-y-auto divide-y divide-slate-100">
                                        {rosterLoading ? (
                                            <div className="p-6 text-center text-sm text-slate-500">Loading roster...</div>
                                        ) : roster.length === 0 ? (
                                            <div className="p-6 text-center text-sm text-slate-500">No students enrolled yet.</div>
                                        ) : roster.map(row => (
                                            <div key={row.student_id} className="flex items-center justify-between gap-3 p-3 hover:bg-slate-50" data-testid={`roster-row-${row.student_id}`}>
                                                <div className="flex items-center gap-3 min-w-0">
                                                    <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">
                                                        {(row.student_name || row.student_email || 'S').charAt(0).toUpperCase()}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <p className="text-sm font-medium text-slate-900 truncate">{row.student_name || 'Unnamed Student'}</p>
                                                        <p className="text-xs text-slate-500 truncate">{row.student_email || row.student_id}</p>
                                                        {row.student_is_active === false && (
                                                            <p className="text-xs text-red-500">Inactive user</p>
                                                        )}
                                                    </div>
                                                </div>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => onRemoveStudent(row)}
                                                    disabled={removingStudentId === row.student_id}
                                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                    aria-label={`Remove ${row.student_name || row.student_email || 'student'} from roster`}
                                                    title={`Remove ${row.student_name || row.student_email || 'student'} from roster`}
                                                    data-testid={`roster-remove-${row.student_id}`}
                                                >
                                                    <Trash2 className="w-4 h-4" aria-hidden="true" />
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function OversightPanel({ oversight }) {
    if (!oversight) {
        return <div className="flex items-center justify-center h-32"><div className="w-8 h-8 border-4 border-[#2F8FCB] border-t-transparent rounded-full animate-spin" /></div>;
    }

    const { coa, reconciliation: recon, counts } = oversight;

    return (
        <div className="space-y-4" data-testid="oversight-panel">
            {/* COA Health */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                <div className="flex items-center gap-3 mb-4">
                    {coa.ok ? <CheckCircle2 className="w-6 h-6 text-emerald-500" /> : <AlertCircle className="w-6 h-6 text-amber-500" />}
                    <div>
                        <h3 className="font-semibold text-slate-900">Chart of Accounts</h3>
                        <p className="text-xs text-slate-500">{coa.total_accounts} total, {coa.active_accounts} active</p>
                    </div>
                    <span className={`ml-auto px-3 py-1 rounded-full text-xs font-medium ${coa.ok ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                        {coa.ok ? 'Healthy' : `${coa.issues.length} issue(s)`}
                    </span>
                </div>
                {coa.issues.length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                        {coa.issues.map((issue, i) => (
                            <div key={i} className="flex items-start gap-2 text-sm text-amber-800">
                                <ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                <span><strong>{issue.label}:</strong> {issue.issue} (code: {issue.code.substring(0, 50)}{issue.code.length > 50 ? '...' : ''})</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Reconciliation */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ReconCard title="AR Reconciliation" data={recon.ar} />
                <ReconCard title="AP Reconciliation" data={recon.ap} />
            </div>

            {/* Counts */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                <h3 className="font-semibold text-slate-900 mb-3">Transaction Counts</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {Object.entries(counts).map(([key, val]) => (
                        <div key={key} className="bg-slate-50 rounded-lg p-3 text-center">
                            <p className="text-lg font-bold text-slate-900">{val}</p>
                            <p className="text-xs text-slate-500 capitalize">{key.replace(/_/g, ' ')}</p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function ReconCard({ title, data }) {
    return (
        <div className={`bg-white rounded-xl border shadow-sm p-5 ${data.matched ? 'border-emerald-200' : 'border-red-200'}`}>
            <div className="flex items-center gap-2 mb-3">
                {data.matched ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <AlertCircle className="w-5 h-5 text-red-500" />}
                <h3 className="font-semibold text-slate-900 text-sm">{title}</h3>
                <span className={`ml-auto px-2 py-0.5 rounded-full text-xs font-medium ${data.matched ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                    {data.matched ? 'MATCHED' : 'MISMATCH'}
                </span>
            </div>
            <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Outstanding</span><span className="font-mono">{data.outstanding.toLocaleString()}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">GL Balance</span><span className="font-mono">{data.gl_balance.toLocaleString()}</span></div>
                {!data.matched && (
                    <div className="flex justify-between text-red-600 font-medium"><span>Variance</span><span className="font-mono">{data.variance.toLocaleString()}</span></div>
                )}
            </div>
        </div>
    );
}

function SchoolsPanel({ schools, newSchoolName, setNewSchoolName, creatingSchool, onCreateSchool, onEditSchool, onToggleSchool, togglingSchool }) {
    return (
        <div className="space-y-4" data-testid="schools-panel">
            {/* Create School */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Building2 className="w-5 h-5" /> Create New School
                </h3>
                <div className="flex gap-3 items-end">
                    <div className="flex-1">
                        <Label>School Name</Label>
                        <Input
                            value={newSchoolName}
                            onChange={(e) => setNewSchoolName(e.target.value)}
                            placeholder="e.g., University of the Philippines"
                            className="mt-1"
                            data-testid="new-school-name"
                        />
                    </div>
                    <Button onClick={onCreateSchool} disabled={creatingSchool || !newSchoolName.trim()} className="bg-[#2F8FCB]" data-testid="create-school-btn">
                        {creatingSchool ? 'Creating...' : 'Create School'}
                    </Button>
                </div>
            </div>

            {/* School List */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-200 bg-slate-50">
                    <h3 className="font-semibold text-sm text-slate-900 flex items-center gap-2">
                        <Building2 className="w-4 h-4" /> Schools ({schools.length})
                    </h3>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                            <tr>
                                <th className="text-left px-4 py-2.5">Name</th>
                                <th className="text-left px-4 py-2.5">Slug</th>
                                <th className="text-center px-4 py-2.5">Users</th>
                                <th className="text-center px-4 py-2.5">Status</th>
                                <th className="text-left px-4 py-2.5">Created</th>
                                <th className="text-center px-4 py-2.5">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {schools.map(s => (
                                <tr key={s.id} className="hover:bg-slate-50" data-testid={`school-row-${s.id}`}>
                                    <td className="px-4 py-2.5 font-medium text-slate-900">{s.name}</td>
                                    <td className="px-4 py-2.5 text-slate-500 font-mono text-xs">{s.slug}</td>
                                    <td className="px-4 py-2.5 text-center">
                                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">{s.user_count}</span>
                                    </td>
                                    <td className="px-4 py-2.5 text-center">
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                            {s.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td className="px-4 py-2.5 text-slate-500 text-xs">{s.created_at ? new Date(s.created_at).toLocaleDateString() : '-'}</td>
                                    <td className="px-4 py-2.5 text-center">
                                        <div className="flex items-center justify-center gap-1">
                                            <Button variant="ghost" size="sm" className="h-8 text-xs text-slate-600" onClick={() => onEditSchool(s)} data-testid={`edit-school-${s.id}`}>
                                                <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className={`h-8 text-xs ${s.is_active ? 'text-red-600' : 'text-emerald-600'}`}
                                                onClick={() => onToggleSchool(s)}
                                                disabled={togglingSchool === s.id}
                                                data-testid={`toggle-school-${s.id}`}
                                            >
                                                {s.is_active ? <PowerOff className="w-3.5 h-3.5 mr-1" /> : <Power className="w-3.5 h-3.5 mr-1" />}
                                                {s.is_active ? 'Deactivate' : 'Activate'}
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

class SchoolsErrorBoundary extends Component {
    state = { hasError: false };
    static getDerivedStateFromError() { return { hasError: true }; }
    componentDidCatch(error, info) { console.error('SchoolsPanel error:', error, info); }
    render() {
        if (this.state.hasError) return (
            <div className="p-8 text-center text-slate-500">
                Failed to load schools.
                <button onClick={() => this.setState({ hasError: false })} className="ml-2 text-[#2F8FCB] underline">Retry</button>
            </div>
        );
        return this.props.children;
    }
}

function AuditLogPanel({ logs, total, offset, setOffset, search, setSearch, dateFrom, setDateFrom, dateTo, setDateTo }) {
    const pageSize = 50;
    const totalPages = Math.ceil(total / pageSize);
    const currentPage = Math.floor(offset / pageSize) + 1;

    return (
        <div className="space-y-4" data-testid="audit-log-tab">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                        placeholder="Search user, action, or description..."
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setOffset(0); }}
                        className="pl-9 h-9 text-sm"
                        data-testid="audit-search-input"
                    />
                </div>
                <div className="flex gap-2">
                    <Input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => { setDateFrom(e.target.value); setOffset(0); }}
                        className="h-9 text-xs w-36"
                        placeholder="From"
                    />
                    <Input
                        type="date"
                        value={dateTo}
                        onChange={(e) => { setDateTo(e.target.value); setOffset(0); }}
                        className="h-9 text-xs w-36"
                        placeholder="To"
                    />
                </div>
            </div>

            <p className="text-xs text-slate-400">{total} total entries</p>

            {/* Table */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                            <tr>
                                <th className="text-left px-4 py-2.5 w-40">Timestamp</th>
                                <th className="text-left px-4 py-2.5">User</th>
                                <th className="text-left px-4 py-2.5">Action</th>
                                <th className="text-left px-4 py-2.5">Entity</th>
                                <th className="text-left px-4 py-2.5">Description</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {logs.length === 0 ? (
                                <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">No audit log entries found</td></tr>
                            ) : (
                                logs.map((log, i) => (
                                    <tr key={i} className="hover:bg-slate-50">
                                        <td className="px-4 py-2 text-xs text-slate-500 whitespace-nowrap">
                                            {log.timestamp ? new Date(log.timestamp).toLocaleString() : '-'}
                                        </td>
                                        <td className="px-4 py-2 text-xs font-medium text-slate-900">{log.user_name || '-'}</td>
                                        <td className="px-4 py-2">
                                            <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-700 uppercase">{log.action}</span>
                                        </td>
                                        <td className="px-4 py-2 text-xs text-slate-500">{log.entity_type}</td>
                                        <td className="px-4 py-2 text-xs text-slate-600 max-w-xs truncate">{log.details}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between bg-slate-50">
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={offset === 0}
                            onClick={() => setOffset(Math.max(0, offset - pageSize))}
                            className="h-7 text-xs"
                        >
                            <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Prev
                        </Button>
                        <span className="text-xs text-slate-500">Page {currentPage} of {totalPages}</span>
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={offset + pageSize >= total}
                            onClick={() => setOffset(offset + pageSize)}
                            className="h-7 text-xs"
                        >
                            Next <ChevronRight className="w-3.5 h-3.5 ml-1" />
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}
